/**
 * Один запрос на всех, кому нужен один и тот же ответ.
 *
 * В терминале две панели ордера — по одной на ногу, — и каждая опрашивала
 * `/api/balances` сама. Список символов тянулся трижды: сам терминал дважды и
 * командная палитра. Ответ у всех одинаковый, он не зависит ни от ноги, ни от
 * того, кто спросил, — а бэкенд однопоточный, и лишние запросы стоят места в
 * очереди, где стоит история графика.
 *
 * Здесь две вещи, и обе про «не спрашивать дважды»:
 *   - запрос в полёте отдаётся всем, кто попросил, пока он летит;
 *   - свежий ответ живёт `ttlMs` и отдаётся из памяти.
 *
 * Только для GET-ов, у которых ответ общий для всей страницы. Что-то, что
 * зависит от параметров вызывающего, сюда класть нельзя — ключ здесь URL, и
 * два разных смысла с одним URL склеятся в один ответ.
 */
import { apiClient } from '../api/client';

interface Entry {
  ts: number;
  /** Промис живёт и после ответа: пока он в пределах TTL, это и есть кеш. */
  promise: Promise<any>;
}

const cache = new Map<string, Entry>();

/** Адрес ставок фандинга символа. Один на всех — ключ кеша здесь URL, и
 *  `?symbol=A/B` против `?symbol=A%2FB` были бы двумя разными запросами. */
export const fundingUrl = (sym: string) => `/api/funding?symbol=${encodeURIComponent(sym)}`;

/** Позиции с бирж: общий адрес для панелей терминала, вкладки «Закрыть»,
 *  портфеля и страницы сессий. */
export const POSITIONS_URL = '/api/positions';

/** Журнал событий: его опрашивают колокольчик (раз в 15 с) и поздравлялка
 *  (раз в 30 с). Через sharedGet поздравлялка берёт ответ колокольчика. */
export const NOTIFICATIONS_URL = '/api/notifications?limit=20';

/** Забыть ответ: после действия, которое меняет данные на сервере (ручная
 *  синхронизация позиций), следующий запрос обязан уйти в сеть. */
export function invalidateShared(url: string): void {
  cache.delete(url);
}

export function sharedGet<T = any>(url: string, ttlMs = 5000): Promise<T> {
  const hit = cache.get(url);
  if (hit && Date.now() - hit.ts < ttlMs) return hit.promise as Promise<T>;

  const promise = apiClient.raw.get(url).then(r => r.data).catch(err => {
    // Ошибку не кешируем: иначе одна неудачная попытка гасила бы всех
    // подписчиков до конца TTL, а бэкенд к тому времени уже поднялся.
    cache.delete(url);
    throw err;
  });
  cache.set(url, { ts: Date.now(), promise });
  return promise as Promise<T>;
}
